/**
 * Adapter hook bridging Evolu data + useCashuWallet + useCashuReceive into ReceiveAdapter.
 *
 * This is the glue layer between the composition-only ReceivePage and the core
 * Cashu receive hook. It creates a WalletFactory and Persistence interface
 * backed by Evolu, then delegates to useCashuReceive for the actual receive logic.
 *
 * SECURITY: Never logs token secrets or proof data.
 *
 * Tier: 1 (beginner) -- core receive infrastructure.
 *
 * @module cashu/hooks/useCashuReceiveAdapter
 */

import { useMemo, useCallback, useRef } from 'react';
import { useEvolu, useQuery } from '@evolu/react';
import { NonEmptyString1000, SqliteBoolean, getOrThrow } from '@evolu/common';
import type { Evolu } from '@evolu/common';
import { Wallet } from '@cashu/cashu-ts';
import type { SatsflowSchema } from '@/db/database';
import { useCashuReceive } from '@/features/cashu/hooks/useCashuReceive';
import type {
  WalletFactory,
  CashuReceivePersistence,
} from '@/features/cashu/hooks/useCashuReceive';
import type { EvoluTokenRow } from '@/features/cashu/utils/proof-management';
import type { ReceiveAdapter, ReceiveResult } from '@/types/adapters';

// ── Typed Evolu Helper ───────────────────────────────────────────────────────

/**
 * Cast the generic Evolu instance to the app's typed schema.
 *
 * @param evolu - Generic Evolu instance from useEvolu()
 * @returns Typed Evolu instance for SatsflowSchema
 */
function typedEvolu(evolu: ReturnType<typeof useEvolu>): Evolu<SatsflowSchema> {
  return evolu as unknown as Evolu<SatsflowSchema>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Adapter hook that bridges Evolu data into useCashuReceive and exposes ReceiveAdapter.
 *
 * Creates a WalletFactory that instantiates cashu-ts Wallet instances for any
 * mint URL, and CashuReceivePersistence callbacks that store received proofs
 * and transactions in Evolu. Returns a ReceiveAdapter compatible with the
 * send-receive feature.
 *
 * @returns ReceiveAdapter for Cashu ecash receives
 */
export function useCashuReceiveAdapter(): ReceiveAdapter {
  const evolu = useEvolu();
  const allMintsQueryRef = useRef(
    evolu.createQuery((db) => db.selectFrom('cashuMint').selectAll()),
  );
  const allMintsQuery = allMintsQueryRef.current;
  const mintRows = useQuery(allMintsQuery);
  const walletCacheRef = useRef<Map<string, Wallet>>(new Map());

  // Build WalletFactory that creates/caches wallets per mint URL
  const walletFactory: WalletFactory = useMemo(
    () => ({
      getWallet: async (mintUrl: string): Promise<Wallet | null> => {
        try {
          const cached = walletCacheRef.current.get(mintUrl);
          if (cached) {
            return cached;
          }

          const wallet = new Wallet(mintUrl);
          await wallet.loadMint();
          walletCacheRef.current.set(mintUrl, wallet);
          return wallet;
        } catch {
          return null;
        }
      },
    }),
    [],
  );

  // Build Persistence callbacks
  const persistence: CashuReceivePersistence = useMemo(() => {
    const db = typedEvolu(evolu);

    return {
      storeReceivedProofs: (proofs: EvoluTokenRow[]): void => {
        for (const proof of proofs) {
          db.insert('cashuToken', {
            mintId: getOrThrow(NonEmptyString1000.from(proof.mintId)),
            amount: proof.amount,
            secret: getOrThrow(NonEmptyString1000.from(proof.secret)),
            c: getOrThrow(NonEmptyString1000.from(proof.c)),
            keysetId: getOrThrow(NonEmptyString1000.from(proof.keysetId)),
            state: getOrThrow(NonEmptyString1000.from('active')),
            lockedTo: proof.lockedTo ? getOrThrow(NonEmptyString1000.from(proof.lockedTo)) : null,
            spentAt: null,
            spentInTxId: null,
          });
        }
      },

      insertTransaction: (tx): void => {
        db.insert('transaction', {
          type: getOrThrow(NonEmptyString1000.from(tx.type)),
          status: getOrThrow(NonEmptyString1000.from('settled')),
          amountSats: tx.amountSats,
          direction: getOrThrow(NonEmptyString1000.from(tx.direction)),
          mintId: tx.mintId ? getOrThrow(NonEmptyString1000.from(tx.mintId)) : null,
          kind: getOrThrow(NonEmptyString1000.from(tx.kind)),
          initiatedAt: getOrThrow(NonEmptyString1000.from(tx.settledAt)),
          settledAt: getOrThrow(NonEmptyString1000.from(tx.settledAt)),
          custodyType: getOrThrow(NonEmptyString1000.from(tx.custodyType)),
          exportedToSatsflow: getOrThrow(SqliteBoolean.from(0)),
          exportedToClams: getOrThrow(SqliteBoolean.from(0)),
          feeSats: null,
          paymentHash: null,
          bolt11: null,
          bolt12Offer: null,
          preimage: null,
          cashuToken: null,
          contactId: null,
          categoryId: null,
          note: tx.note ? getOrThrow(NonEmptyString1000.from(tx.note)) : null,
          lightningAddress: null,
          externalId: null,
          expiresAt: null,
        });
      },

      getMintIdForUrl: (mintUrl: string): string | null => {
        const mint = mintRows.find((m) => m.url === mintUrl && !m.isDeleted);
        return mint ? String(mint.id) : null;
      },
    };
  }, [evolu, mintRows]);

  // Delegate to core receive hook
  const receiveHook = useCashuReceive(walletFactory, persistence);

  // Build the receive function that maps useCashuReceive result to ReceiveResult
  const receive = useCallback(
    async (tokenString: string, note?: string): Promise<ReceiveResult> => {
      const result = await receiveHook.receive(tokenString, note);

      if (result === null) {
        return {
          success: false,
          error: receiveHook.error ?? 'Receive failed',
        };
      }

      return {
        success: true,
        amount: result.amount,
        sourceLabel: result.mintUrl,
      };
    },
    [receiveHook],
  );

  return {
    type: 'cashu',
    label: 'Ecash',
    receive,
  };
}
